/* eslint-disable react/no-multi-comp */
import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Select,
    Button,
    Row,
    Col,
    Tooltip,
    Space,
    Divider,
    TreeSelect,
} from '@app/ui';
import { capitalize } from '@app/utils/capitalize';
import {
    getIdsOptions,
    getParamsOptions,
    baseParamOf,
    makeRowKey,
} from './options';
import InputTag from '@app/components/filters/components/InputTag';
import DatePicker from './DatePicker';
import NumberPicker from './NumberPicker';
import SocialHandlePicker from './SocialHandlePicker';
import { parseSelectedRightsholderPlacements } from '@app/utils/parseSelectedPlacements';
import connect from '@app/utils/connect';
import getTruthyKeys from '@app/utils/getTruthyKeys';

const { SHOW_PARENT } = TreeSelect;

const { Option } = Select;

// Types
interface AutomatedParamProps {
    p?: string | null;
    setActiveParam: (param: string) => void;
    removeActiveParam: (param: string) => () => void;
    activeParams?: string[];
    setSelectedParam: ({
        param,
        sub,
    }: {
        param: string;
        sub?: string;
    }) => (value: any) => void;
    onBlur: (param: string) => () => void;
    allSelectedParams: Record<string, any>;
    toggleActive?: () => void;
    disabled?: boolean;
    filters: any;
    client: any;
    entityType: string;
    error?: string | null;
    /** Base parameters already used by other rows in this row's group — disabled in
     *  the picker so a group can't hold two conditions of the same type. */
    disabledParams?: string[];
}

type ParamType =
    | 'id'
    | 'compoundId'
    | 'dateRange'
    | 'numberRange'
    | 'floatRange'
    | 'text'
    | 'usernamesObject';

interface ParamOption {
    value: string;
    name: string;
    type: string;
    placeholder?: string;
    entities?: string[];
}

// Component for rendering different parameter types
const ParamRenderer: React.FC<{
    type: string;
    param: string;
    selectedParams: any;
    setSelectedParam: ({
        param,
        sub,
    }: {
        param: string;
        sub?: string;
    }) => (value: any) => void;
    onBlur: (param: string) => () => void;
    disabled: boolean;
    automatedParamOptions: any[];
    paramOption: ParamOption;
    allSelectedParams: Record<string, any>;
    client: any;
    t: any;
}> = ({
    type,
    param,
    selectedParams,
    setSelectedParam,
    onBlur,
    disabled,
    automatedParamOptions,
    paramOption,
    allSelectedParams,
    client,
    t,
}) => {
    // `param` is the row key (storage); `base` is the parameter value used for
    // type-specific behaviour like rightsholder-placement nesting (SP-8141).
    const base = paramOption?.value;
    // Transform options for TreeSelect (for compound ID case)
    const treeSelectOptions = useMemo(() => {
        // Only process if automatedParamOptions is an array
        if (!Array.isArray(automatedParamOptions)) {
            return [];
        }

        return automatedParamOptions.map(option => {
            const hasChildren =
                Array.isArray(option?.nested) && option.nested.length > 0;
            const newNestedOptions = hasChildren
                ? option.nested.map(nestedOption => {
                      return {
                          value: `${option.value}-${nestedOption.value}`,
                          title: nestedOption.label,
                      };
                  })
                : undefined;

            return {
                value:
                    base === 'rightsholderPlacements' && hasChildren
                        ? `rh_${option.value}`
                        : `${option.value}`,
                title: option.label,
                children: newNestedOptions,
            };
        });
    }, [automatedParamOptions]);

    const hasNestedPlacements = useMemo(() => {
        return (
            Array.isArray(automatedParamOptions) &&
            automatedParamOptions.some(
                (o: any) => Array.isArray(o?.nested) && o.nested.length > 0,
            )
        );
    }, [automatedParamOptions]);

    const treeCheckedValues = useMemo(() => {
        if (base !== 'rightsholderPlacements') return [] as string[];
        const flattened: string[] = Array.isArray(selectedParams)
            ? selectedParams
            : selectedParams && typeof selectedParams === 'object'
              ? getTruthyKeys(selectedParams)
              : [];
        const flatSet = new Set(flattened.map(v => `${v}`));
        if (!Array.isArray(automatedParamOptions)) return flattened;
        if (!hasNestedPlacements) return flattened;

        const checked: string[] = [];
        automatedParamOptions.forEach((option: any) => {
            const rid = `${option?.value}`;
            const children = Array.isArray(option?.nested) ? option.nested : [];
            children.forEach((child: any) => {
                const raw = `${child?.value}`;
                const nodeValue = `${rid}-${raw}`;
                if (!raw.includes(',')) {
                    if (flatSet.has(nodeValue)) checked.push(nodeValue);
                } else {
                    const pids = raw.split(',');
                    const allSelected = pids.every(pid =>
                        flatSet.has(`${rid}-${pid}`),
                    );
                    if (allSelected) checked.push(nodeValue);
                }
            });
        });
        return checked;
    }, [param, selectedParams, automatedParamOptions]);

    const renderSelect = (mode: 'multiple' | undefined = undefined) => {
        // Ensure selectedParams is safe for Select/TreeSelect
        const safeSelectedParams = selectedParams
            ? Array.isArray(selectedParams)
                ? selectedParams
                : getTruthyKeys(selectedParams)
            : mode === 'multiple'
              ? []
              : undefined;

        // For compound ID (platforms), use TreeSelect
        if (type === 'compoundId') {
            return (
                <TreeSelect
                    treeCheckable
                    showCheckedStrategy={SHOW_PARENT}
                    style={{ width: '100%' }}
                    placeholder={t(
                        `tools:collections.parameterNames.${paramOption.value}`,
                    )}
                    treeData={treeSelectOptions}
                    value={
                        base === 'rightsholderPlacements' &&
                        hasNestedPlacements
                            ? treeCheckedValues
                            : safeSelectedParams
                    }
                    onChange={value => {
                        if (base === 'rightsholderPlacements') {
                            setSelectedParam({ param })(
                                parseSelectedRightsholderPlacements(
                                    value,
                                    automatedParamOptions,
                                ),
                            );
                        } else {
                            setSelectedParam({ param })(value);
                        }
                    }}
                    treeNodeFilterProp="title"
                    autoClearSearchValue={false}
                    showSearch
                    allowClear
                    maxTagCount={7}
                    maxTagTextLength={8}
                    maxTagPlaceholder={`...`}
                    disabled={disabled || !treeSelectOptions.length}
                />
            );
        }

        // For regular ID, use regular Select
        return (
            <Select
                showSearch
                optionFilterProp="label"
                mode={mode}
                style={{ width: '100%' }}
                placeholder={t(
                    `tools:collections.parameterNames.${paramOption.value}`,
                )}
                value={safeSelectedParams}
                onChange={setSelectedParam({ param })}
                onBlur={onBlur(param)}
                disabled={disabled || !automatedParamOptions.length}
                options={automatedParamOptions}
            />
        );
    };

    const renderTextInput = () => {
        // Ensure selectedParams is an array for InputTag
        const safeSelectedParams = Array.isArray(selectedParams)
            ? selectedParams
            : [];

        return (
            <Space style={{ width: '100%' }}>
                {paramOption.value === 'hashtags' && <span>#</span>}
                <div style={{ width: '100%' }}>
                    <InputTag
                        value={safeSelectedParams}
                        onChange={setSelectedParam({ param })}
                        // SP-8586: captions may contain spaces, so only Enter
                        // submits one — same behavior as the data-explorer
                        // handle filter. Hashtags keep space-tokenizing.
                        isHandleInput={paramOption.value === 'postCaption'}
                        placeholderText={
                            !disabled
                                ? t(
                                      `tools:automatedParams.placeholders.${
                                          paramOption.value === 'hashtags'
                                              ? 'enterHashtags'
                                              : 'enterCaptions'
                                      }`,
                                  )
                                : ''
                        }
                        maxCount={disabled ? 0 : undefined}
                    />
                </div>
                <Tooltip
                    title={
                        paramOption.value === 'hashtags'
                            ? t('tools:automatedParams.tooltips.hashtagHelper')
                            : t('tools:automatedParams.tooltips.captionHelper')
                    }
                >
                    <i className="fas fa-info-circle" />
                </Tooltip>
            </Space>
        );
    };

    const renderUsernameObject = () => {
        // Ensure selectedParams is an array and has at least one item
        let safeSelectedParams = Array.isArray(selectedParams)
            ? selectedParams
            : [];

        // If no items, create a default one
        if (safeSelectedParams.length === 0) {
            safeSelectedParams = [{ platformId: null, names: [] }];
        }

        return (
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
                {safeSelectedParams.map(
                    (socialPlatformSelections: any, ind: number) => (
                        <SocialHandlePicker
                            key={socialPlatformSelections.platformId || ind}
                            socialPlatformSelections={socialPlatformSelections}
                            selectedParams={safeSelectedParams}
                            socialHandlerIndex={ind}
                            clientId={client.id}
                            options={automatedParamOptions}
                            param={param}
                            setSelectedParam={setSelectedParam}
                            disabled={disabled}
                        />
                    ),
                )}
            </Space>
        );
    };

    const renderRangePicker = (
        component: 'DatePicker' | 'NumberPicker',
        props?: any,
    ) => {
        const Component =
            component === 'DatePicker' ? DatePicker : NumberPicker;
        return (
            <Component
                param={param}
                setSelectedParam={setSelectedParam}
                allSelectedParams={allSelectedParams}
                disabled={disabled}
                {...props}
            />
        );
    };

    // Render based on type
    switch (type as ParamType) {
        case 'id':
            return renderSelect('multiple');
        case 'compoundId':
            return renderSelect(); // TreeSelect handles its own mode
        case 'dateRange':
            return renderRangePicker('DatePicker');
        case 'numberRange':
            return renderRangePicker('NumberPicker');
        case 'floatRange':
            return renderRangePicker('NumberPicker', {
                type: 'perc',
                max: 100,
            });
        case 'text':
            return renderTextInput();
        case 'usernamesObject':
            return renderUsernameObject();
        default:
            return null;
    }
};

const AutomatedParam: React.FC<AutomatedParamProps> = ({
    p = null,
    setActiveParam,
    removeActiveParam,
    activeParams = [],
    setSelectedParam,
    onBlur,
    allSelectedParams,
    toggleActive,
    disabled = false,
    filters,
    client,
    entityType,
    error,
    disabledParams = [],
}) => {
    const { t } = useTranslation(['tools', 'common']);
    // Check if p is an emptyParam identifier
    const isEmptyParam = typeof p === 'string' && p.startsWith('emptyParam');
    const [param, setParam] = useState<string | null>(
        disabled && p && !isEmptyParam ? p : isEmptyParam ? null : null,
    );
    const { hasSeveralRightsholders } = filters;

    // The parameter value behind this row's key ('sponsors' for both 'sponsors'
    // and a duplicate 'sponsors#2'). Used for option/type/label resolution while
    // `param` (the row key) remains the form-state storage key. See SP-8141.
    const base = baseParamOf(param);

    // Effects
    useEffect(() => {
        // Don't set param if it's an emptyParam identifier or empty string
        if (!p || p === param) return;
        if (typeof p === 'string' && p.startsWith('emptyParam')) return;
        setParam(p);
    }, [p, param]);

    useEffect(() => {
        // Tweak for account usernames
        if (
            base === 'accountUsernames' &&
            (!allSelectedParams[param] ||
                allSelectedParams[param].length === 0)
        ) {
            setSelectedParam({ param })([{ platformId: null, names: [] }]);
        }
    }, [param, base, allSelectedParams, setSelectedParam]);

    // Memoized values
    const options = useMemo(
        () => getIdsOptions({ filters, client, type: entityType }),
        [filters, client, entityType],
    );

    const paramsOptions = getParamsOptions(entityType);

    const selectedParams = useMemo(() => {
        if (!param) return [];
        const selected = allSelectedParams[param];

        // Handle usernamesObject case - ensure it's always an array
        if (base === 'accountUsernames') {
            return Array.isArray(selected) ? selected : [];
        }

        if (base === 'rightsholderPlacements') {
            return parseSelectedRightsholderPlacements(
                selected,
                options[base],
            );
        }

        // Handle id and compoundId types - convert objects to arrays for Select/TreeSelect
        const paramOption = paramsOptions.find(po => po.value === base);
        const type = paramOption?.type;

        if (type === 'id' || type === 'compoundId') {
            if (
                selected &&
                typeof selected === 'object' &&
                !Array.isArray(selected)
            ) {
                // Convert {1: true, 2: true, 3: true} to ["1", "2", "3"]
                return Object.entries(selected)
                    .filter(([, value]) => value === true)
                    .map(([key]) => key);
            }
        }

        return selected;
    }, [
        param,
        base,
        allSelectedParams,
        hasSeveralRightsholders,
        options,
        paramsOptions,
    ]);

    const automatedParamOptions = useMemo(() => {
        const paramOptions = options?.[base];

        // Handle usernamesObject case - extract platforms array from object
        if (
            base === 'accountUsernames' &&
            paramOptions &&
            typeof paramOptions === 'object' &&
            'platforms' in paramOptions
        ) {
            return paramOptions.platforms || [];
        }

        // Handle regular array cases
        return Array.isArray(paramOptions) ? paramOptions : [];
    }, [options, base]);

    const showName = useMemo(() => {
        if (!base) return;
        return t(`tools:collections.parameterNames.${base}`);
    }, [base, t]);

    const isActive = !!param;
    const paramOption = paramsOptions.find(po => po.value === base);
    const type = paramOption?.type;

    // Event handlers — selecting a parameter from the dropdown. The same parameter
    // may already be used by another row, so derive a unique row key (SP-8141);
    // duplicates get a '#N' suffix, the first occurrence keeps the bare value.
    const handleParamChange = (chosenParam: string) => {
        const rowKey = makeRowKey(chosenParam, activeParams);
        setParam(rowKey);
        setActiveParam(rowKey);
    };

    const handleRemove = () => {
        // If we have a param, remove it. Otherwise, remove the emptyParam identifier from props
        if (param) {
            return removeActiveParam(param);
        }
        // If no param is set but we have an emptyParam identifier, remove that
        if (p && typeof p === 'string' && p.startsWith('emptyParam')) {
            return removeActiveParam(p);
        }
        return () => {}; // Return empty function if param is null
    };

    return (
        <>
            <Row gutter={16} align="middle">
                <Col flex="250px">
                    {!disabled ? (
                        <Select
                            style={{ width: '100%' }}
                            placeholder={t(
                                'tools:automatedParams.placeholders.selectParam',
                            )}
                            value={base}
                            onChange={handleParamChange}
                            disabled={disabled}
                        >
                            {/* SP-8141: the same parameter may be added to
                                multiple rows (e.g. two Sponsors conditions
                                across OR branches) — but not twice within one
                                group, so options taken by a group sibling are
                                disabled. */}
                            {paramsOptions.map(option => (
                                <Option
                                    key={option.value}
                                    value={option.value}
                                    disabled={disabledParams.includes(
                                        option.value,
                                    )}
                                >
                                    {t(
                                        `tools:collections.parameterNames.${option.value}`,
                                    )}
                                </Option>
                            ))}
                        </Select>
                    ) : (
                        <Button
                            type={isActive ? 'primary' : 'default'}
                            style={{ width: '100%' }}
                            onClick={toggleActive}
                            disabled={disabled}
                        >
                            {capitalize(showName || '')}
                        </Button>
                    )}
                </Col>

                <Col flex="1">
                    {isActive && paramOption && (
                        <>
                            <ParamRenderer
                                type={type}
                                param={param}
                                selectedParams={selectedParams}
                                setSelectedParam={setSelectedParam}
                                onBlur={onBlur}
                                disabled={disabled}
                                automatedParamOptions={automatedParamOptions}
                                paramOption={paramOption}
                                allSelectedParams={allSelectedParams}
                                client={client}
                                t={t}
                            />
                            {error && (
                                <div
                                    style={{
                                        color: '#ff4d4f',
                                        fontSize: '14px',
                                        marginTop: '4px',
                                    }}
                                >
                                    {error}
                                </div>
                            )}
                        </>
                    )}
                </Col>

                <Col flex="50px">
                    {!disabled && (
                        <Tooltip
                            title={t('tools:automatedParams.buttons.remove')}
                        >
                            <Button
                                size="small"
                                icon={<i className="fa-regular fa-trash" />}
                                onClick={handleRemove()}
                                type="primary"
                                color="default"
                                variant="solid"
                            />
                        </Tooltip>
                    )}
                </Col>
            </Row>
            <Divider style={{ margin: '12px 0', opacity: 0.5 }} />
        </>
    );
};

export default connect(['filters', 'client'])(AutomatedParam);
